import { useState } from "react";
import {
  Calendar, User, ChevronDown, ChevronUp, CheckCircle2, StickyNote, Scale,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { PRIORITY, EFFORT, TASK_STATUSES, URGENCY, IMPACT_TYPE_TONE } from "@/lib/constants";

const _band = (b) => {
  if (!b) return null;
  const [lo, hi] = b;
  return (lo || 0) === (hi || 0) ? `$${(lo || 0).toLocaleString()}` : `$${(lo || 0).toLocaleString()}–$${(hi || 0).toLocaleString()}`;
};

export function TaskCard({ task, onUpdate, onAddNote, index }) {
  const [open, setOpen] = useState(false);
  const [assignee, setAssignee] = useState(task.assignee || "");
  const [deadline, setDeadline] = useState(task.deadline || "");
  const [estimate, setEstimate] = useState(task.estimated_value ?? "");
  const [recovered, setRecovered] = useState(task.recovered_value ?? "");
  const [noteText, setNoteText] = useState("");

  const p = PRIORITY[task.priority] || PRIORITY.Low;
  const u = URGENCY[task.urgency];
  const tone = IMPACT_TYPE_TONE[task.impact_type];
  const today = new Date().toISOString().slice(0, 10);
  const overdue = task.deadline && task.deadline < today && !["DONE", "DISMISSED"].includes(task.status);
  const variance = task.verified && task.recovered_value != null && task.estimated_value != null
    ? Math.round(task.recovered_value - task.estimated_value) : null;

  return (
    <div data-testid={`task-card-${index}`} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        {u && <span data-testid={`task-urgency-${index}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border ${u.cls}`}><span className={`w-1.5 h-1.5 rounded-full ${u.dot}`} />{u.label}</span>}
        {task.impact_type === "COMPLIANCE_RISK" && (
          <span data-testid={`task-compliance-${index}`} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border text-red-700 bg-red-50 border-red-200">
            <Scale className="w-3 h-3" /> Compliance
          </span>
        )}
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold border ${p.cls}`}>{task.priority}</span>
        {task.effort && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold border ${EFFORT[task.effort] || EFFORT.Moderate}`}>{task.effort}</span>}
      </div>
      <p className="font-heading font-semibold text-slate-900 text-sm leading-snug">{task.problem}</p>
      {task.impact_monthly && tone && (
        <div className={`mt-2 rounded-lg border ${tone.bg} px-2.5 py-1.5`} data-testid={`task-impact-${index}`}>
          <span className="text-[9px] font-bold uppercase tracking-wide text-slate-500">{tone.label} · Modeled</span>
          <div className={`text-sm font-bold ${tone.tone} leading-tight`}>{tone.sign}{_band(task.impact_monthly)}<span className="text-[10px] font-medium text-slate-400"> /mo</span> · {tone.sign}{_band(task.impact_annual)}<span className="text-[10px] font-medium text-slate-400"> /yr</span></div>
        </div>
      )}
      {task.impact_note && !task.impact_monthly && <p className="text-xs text-slate-500 mt-1">{task.impact_note}</p>}

      <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-500">
        <span className={`inline-flex items-center gap-1 ${overdue ? "text-red-600 font-semibold" : ""}`}>
          <Calendar className="w-3 h-3" />{task.deadline || "—"}{overdue ? " · overdue" : ""}
        </span>
        {task.assignee && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{task.assignee}</span>}
      </div>

      <div className="mt-3">
        <Select value={task.status} onValueChange={(v) => onUpdate(task.public_id, { status: v })}>
          <SelectTrigger data-testid={`task-status-${index}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TASK_STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <button onClick={() => setOpen((o) => !o)} data-testid={`task-details-${index}`}
        className="mt-3 text-xs font-medium text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1">
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} Details
      </button>

      {open && (
        <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
          {task.why_it_matters && <p className="text-xs text-slate-600"><b className="text-slate-700">Why:</b> {task.why_it_matters}</p>}
          {task.recommended_action && <p className="text-xs text-slate-600"><b className="text-slate-700">Action:</b> {task.recommended_action}</p>}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-500">Assignee</label>
              <Input className="h-8 text-xs" value={assignee} onChange={(e) => setAssignee(e.target.value)}
                onBlur={() => onUpdate(task.public_id, { assignee })} data-testid={`task-assignee-${index}`} placeholder="Name" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Deadline</label>
              <Input type="date" className="h-8 text-xs" value={deadline} onChange={(e) => setDeadline(e.target.value)}
                onBlur={() => onUpdate(task.public_id, { deadline })} />
            </div>
          </div>

          <div>
            <label className="text-[10px] text-slate-500">Your estimated opportunity ($ — an estimate, not a measured result)</label>
            <Input type="number" className="h-8 text-xs" value={estimate} onChange={(e) => setEstimate(e.target.value)}
              onBlur={() => onUpdate(task.public_id, { estimated_value: estimate === "" ? null : Number(estimate) })}
              data-testid={`task-estimate-${index}`} placeholder="e.g. 1500" />
          </div>

          <div>
            <p className="text-[10px] text-slate-500 mb-1 flex items-center gap-1"><StickyNote className="w-3 h-3" /> Notes & evidence</p>
            <div className="space-y-1.5 mb-2">
              {(task.notes || []).map((n, ni) => (
                <div key={ni} className="text-xs text-slate-600 bg-slate-50 rounded-md px-2 py-1.5">
                  {n.text}
                  {n.link && <a href={n.link} target="_blank" rel="noopener noreferrer" className="text-indigo-600 ml-1 underline">link</a>}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input className="h-8 text-xs" value={noteText} onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a note or paste an evidence link…" data-testid={`task-note-input-${index}`} />
              <button onClick={() => {
                if (noteText.trim()) {
                  const link = noteText.match(/https?:\/\/\S+/)?.[0] || null;
                  onAddNote(task.public_id, { text: noteText, link });
                  setNoteText("");
                }
              }} data-testid={`task-note-add-${index}`} className="shrink-0 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs px-3">Add</button>
            </div>
          </div>

          {task.status === "DONE" && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
              {task.estimated_value != null && (
                <p className="text-[10px] text-emerald-700/80 mb-1">Modeled estimate: <b>${Number(task.estimated_value).toLocaleString()}/yr</b> — preserved for comparison.</p>
              )}
              <label className="text-[10px] text-emerald-700 font-semibold">Confirmed recovery ($ — owner-verified actual result only)</label>
              <Input type="number" className="h-8 text-xs mt-1 bg-white" value={recovered} onChange={(e) => setRecovered(e.target.value)}
                onBlur={() => onUpdate(task.public_id, { recovered_value: recovered === "" ? null : Number(recovered) })}
                placeholder="Confirmed savings / revenue" data-testid={`task-recovered-${index}`} />
              {!task.verified ? (
                <button onClick={() => onUpdate(task.public_id, { verified: true })} data-testid={`task-verify-${index}`}
                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold py-2 transition-colors">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Confirm result &amp; archive
                </button>
              ) : (
                <div className="mt-2" data-testid={`task-variance-${index}`}>
                  <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Confirmed &amp; archived</p>
                  {variance != null && (
                    <p className="text-[11px] text-slate-500 mt-0.5">Confirmed ${Number(task.recovered_value).toLocaleString()} vs modeled ${Number(task.estimated_value).toLocaleString()} · variance <b className={variance < 0 ? "text-red-600" : "text-emerald-600"}>{variance < 0 ? "-" : "+"}${Math.abs(variance).toLocaleString()}</b></p>
                  )}
                </div>
              )}
            </div>
          )}

          {task.status !== "DISMISSED" && (
            <button onClick={() => onUpdate(task.public_id, { status: "DISMISSED" })} data-testid={`task-dismiss-${index}`}
              className="text-[11px] text-slate-400 hover:text-red-500 transition-colors">Dismiss task</button>
          )}
        </div>
      )}
    </div>
  );
}
